/**
 * OutcomePanel — "What this buys you" skill-gap visualizer.
 *
 * Renders a per-dimension 0–5 track with now/after/demand markers,
 * a met_before→met_after hero, gaps-closed subhead, proof strip,
 * differentiator, and visible provenance. Ported from
 * prototypes/outcome-panel-prototype.html.
 *
 * Props:
 *   skillMatrix  — dimension keys → current level (0–5)
 *   targetRole   — e.g. "Agentic AI Engineer"
 *   seniority    — e.g. "mid"
 *   track        — "knowledge" | "portfolio"
 *
 * Fetch lifecycle: fires once on mount, renders null until loaded,
 * silently returns null on error (fail-safe).
 */

import React, { useEffect, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Trans, useTranslation } from "react-i18next";
import { fetchOutcome, type OutcomeView } from "../../api/client";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface TrackBarProps {
  now: number;
  after: number;
  demand: number;
  showDemand: boolean;
}

function TrackBar({ now, after, demand, showDemand }: TrackBarProps) {
  const pct = (v: number) => `${(v / 5) * 100}%`;

  return (
    <Box
      sx={{
        position: "relative",
        height: 30,
        minWidth: 0,
      }}
    >
      {/* Base track */}
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          height: 6,
          transform: "translateY(-50%)",
          bgcolor: alpha(theme.palette.text.primary, 0.06),
          borderRadius: 1,
        })}
      />

      {/* Current-level fill (navy) */}
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: "50%",
          left: 0,
          width: pct(now),
          height: 6,
          transform: "translateY(-50%)",
          borderRadius: 1,
          background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          transition: "width 0.5s cubic-bezier(.2,.7,.2,1)",
        })}
      />

      {/* Lift (after − now) in coral/secondary */}
      {after > now && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: "50%",
            left: pct(now),
            width: pct(after - now),
            height: 6,
            transform: "translateY(-50%)",
            borderRadius: 1,
            background: `linear-gradient(90deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
            opacity: 0.9,
            transition: "left 0.5s cubic-bezier(.2,.7,.2,1), width 0.5s cubic-bezier(.2,.7,.2,1)",
          })}
        />
      )}

      {/* Demand marker (gold/warning) — omitted when role not grounded */}
      {showDemand && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: "50%",
            left: pct(demand),
            transform: "translate(-50%, -50%)",
            width: 2,
            height: 18,
            borderRadius: 1,
            bgcolor: theme.palette.warning.main,
            zIndex: 1,
            "&::after": {
              content: '"role"',
              position: "absolute",
              top: -15,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: "0.56rem",
              color: theme.palette.warning.main,
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
            },
          })}
        />
      )}

      {/* Now dot */}
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: "50%",
          left: pct(now),
          width: 13,
          height: 13,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: theme.palette.text.disabled,
          border: `2px solid ${theme.palette.background.paper}`,
          zIndex: 2,
          transition: "left 0.5s cubic-bezier(.2,.7,.2,1)",
        })}
      />

      {/* After dot (success/green) */}
      <Box
        sx={(theme) => ({
          position: "absolute",
          top: "50%",
          left: pct(after),
          width: 13,
          height: 13,
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: theme.palette.success.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.18)}`,
          zIndex: 3,
          transition: "left 0.5s cubic-bezier(.2,.7,.2,1)",
        })}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend({ showDemand }: { showDemand: boolean }) {
  const { t } = useTranslation("outcome");
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={2}
      sx={(theme) => ({ mb: 2.5, color: theme.palette.text.secondary })}
    >
      <LegendItem dotColor="text.disabled" label={t("OutcomePanel.legendYouNow")} />
      <LegendItem dotColor="success.main" label={t("OutcomePanel.legendAfterProgram")} glow />
      {showDemand && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={(theme) => ({
              width: 2,
              height: 13,
              borderRadius: 1,
              bgcolor: theme.palette.warning.main,
              flexShrink: 0,
            })}
          />
          <Typography variant="caption" color="text.secondary">
            {t("OutcomePanel.legendRoleDemands")}
          </Typography>
        </Box>
      )}
      <Typography variant="caption" color="text.disabled" sx={{ alignSelf: "center" }}>
        {t("OutcomePanel.legendScale")}
      </Typography>
    </Stack>
  );
}

interface LegendItemProps {
  dotColor: string;
  label: string;
  glow?: boolean;
}

function LegendItem({ dotColor, label, glow }: LegendItemProps) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={(theme) => ({
          width: 11,
          height: 11,
          borderRadius: "50%",
          bgcolor: dotColor,
          flexShrink: 0,
          ...(glow && {
            boxShadow: `0 0 8px ${alpha(theme.palette.success.main, 0.7)}`,
          }),
        })}
      />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface OutcomePanelProps {
  skillMatrix: Record<string, number>;
  targetRole: string;
  seniority: string;
  track: string;
}

export default function OutcomePanel({
  skillMatrix,
  targetRole,
  seniority,
  track,
}: OutcomePanelProps) {
  const { t } = useTranslation("outcome");
  const [outcome, setOutcome] = useState<OutcomeView | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchOutcome({ skill_matrix: skillMatrix, target_role: targetRole, seniority, track })
      .then((data) => {
        if (!cancelled) setOutcome(data);
      })
      .catch(() => {
        // fail-safe: stay null, render nothing
      });
    return () => {
      cancelled = true;
    };
  }, [skillMatrix, targetRole, seniority, track]);

  if (!outcome) return null;

  const {
    role_grounded,
    dimensions,
    met_before,
    met_after,
    total,
    gaps_closed,
    show_deployed_systems,
    expert_path,
    differentiator,
    provenance,
    preview,
  } = outcome;

  const levelsGained = dimensions.reduce((sum, d) => sum + (d.after - d.now), 0);
  const isExpertPath = expert_path || met_before === met_after;
  const subhead = isExpertPath
    ? t("OutcomePanel.subheadExpertPath")
    : gaps_closed.length
      ? t("OutcomePanel.subheadWithGaps", {
          metAfter: met_after,
          total,
          gaps: gaps_closed.slice(0, 3).join(", "),
        })
      : t("OutcomePanel.subheadNoGaps", { metAfter: met_after, total });

  return (
    <Box
      data-testid="outcome-panel"
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        p: { xs: 2.5, md: 3.5 },
        bgcolor: "background.paper",
        boxShadow: theme.shadows[4],
        position: "relative",
        overflow: "hidden",
      })}
    >
      {/* Preview gate banner */}
      {preview && (
        <Alert
          severity="info"
          data-testid="preview-banner"
          sx={{ mb: 2.5 }}
        >
          {t("OutcomePanel.previewBanner")}
        </Alert>
      )}

      {/* Hero */}
      {isExpertPath ? (
        /* Expert path: already role-ready */
        <Box sx={{ mb: 0.5 }}>
          <Typography
            data-testid="outcome-hero"
            component="div"
            sx={(theme) => ({
              fontWeight: 500,
              fontSize: { xs: "2.4rem", md: "3.4rem" },
              lineHeight: 1,
              letterSpacing: "-0.02em",
              color: theme.palette.success.main,
            })}
          >
            <span data-testid="levels-gained">+{levelsGained}</span>
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {t("OutcomePanel.heroExpertCaption", { total })}
          </Typography>
        </Box>
      ) : (
        /* Non-expert path: lead with personalized lift */
        <Box sx={{ mb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
            <Typography
              data-testid="outcome-hero"
              component="div"
              sx={(theme) => ({
                fontWeight: 500,
                fontSize: { xs: "2.4rem", md: "3.4rem" },
                lineHeight: 1,
                letterSpacing: "-0.02em",
                color: theme.palette.success.main,
              })}
            >
              <span data-testid="levels-gained">+{levelsGained}</span>
            </Typography>
            <Typography color="text.secondary">
              {t("OutcomePanel.heroCaption", { total })}
            </Typography>
          </Box>
          {/* Secondary line: met_before → met_after */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", mt: 0.75 }}>
            <Typography
              component="div"
              sx={(theme) => ({
                fontWeight: 500,
                fontSize: { xs: "1.3rem", md: "1.6rem" },
                lineHeight: 1,
                letterSpacing: "-0.01em",
                "& .arrow": { color: theme.palette.success.main, mx: 0.5 },
              })}
            >
              <span data-testid="met-before">{met_before}</span>
              <span className="arrow"> → </span>
              <span data-testid="met-after">{met_after}</span>
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t("OutcomePanel.metCompetencies", { total })}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Subhead */}
      <Typography
        data-testid="outcome-subhead"
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, mt: 0.75 }}
      >
        {subhead}
      </Typography>

      {/* Legend */}
      <Legend showDemand={role_grounded} />

      {/* role_grounded=false notice */}
      {!role_grounded && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", mb: 2, fontStyle: "italic" }}
        >
          {t("OutcomePanel.roleNotGroundedNotice")}
        </Typography>
      )}

      {/* Dimension rows */}
      <Stack gap={0} divider={
        <Box sx={(theme) => ({ borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` })} />
      }>
        {dimensions.map((dim) => {
          const lift = dim.after - dim.now;
          return (
            <Box
              key={dim.key}
              data-testid={`dim-row-${dim.key}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "128px 1fr 44px", md: "182px 1fr 60px" },
                alignItems: "center",
                gap: { xs: 1, md: 2 },
                py: 1.25,
              }}
            >
              {/* Label */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {dim.label}
                  {dim.gap_closed && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={(theme) => ({
                        ml: 0.75,
                        color: theme.palette.success.main,
                        fontWeight: 500,
                      })}
                    >
                      {t("OutcomePanel.gapClosedLabel")}
                    </Typography>
                  )}
                </Typography>
              </Box>

              {/* Track bar */}
              <TrackBar
                now={dim.now}
                after={dim.after}
                demand={dim.demand}
                showDemand={role_grounded}
              />

              {/* Delta */}
              <Typography
                variant="body2"
                sx={(theme) => ({
                  textAlign: "right",
                  fontWeight: 500,
                  color: lift > 0 ? theme.palette.success.main : theme.palette.text.disabled,
                })}
              >
                {lift > 0 ? `+${lift}` : "—"}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Proof strip */}
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.75,
          mt: 3.5,
        })}
      >
        {/* Proof panel */}
        <Box
          data-testid="proof-panel"
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
          })}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.disabled", mb: 1.25 }}
          >
            {t("OutcomePanel.proofPanelTitle")}
          </Typography>
          <Stack gap={1}>
            {show_deployed_systems && (
              <Stack direction="row" gap={1}>
                <Typography component="span" color="success.main" sx={{ flexShrink: 0, fontSize: "0.875rem" }}>▪</Typography>
                <Typography variant="body2">
                  <Trans
                    i18nKey="OutcomePanel.deployedSystemsProof"
                    t={t}
                    components={[<strong key="strong0" />]}
                  />
                </Typography>
              </Stack>
            )}
            <Stack direction="row" gap={1}>
              <Typography component="span" color="success.main" sx={{ flexShrink: 0, fontSize: "0.875rem" }}>▪</Typography>
              <Typography variant="body2">
                {t("OutcomePanel.publicPortfolioProof")}
              </Typography>
            </Stack>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
              <Typography sx={{ fontWeight: 500, fontSize: "1.4rem" }}>~90%</Typography>
              <Typography variant="body2" color="text.secondary">
                <Trans
                  i18nKey="OutcomePanel.ccafProof"
                  t={t}
                  components={[<strong key="strong0" />]}
                />
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Differentiator panel */}
        <Box
          data-testid="differentiator-panel"
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.warning.main, 0.04),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 2,
          })}
        >
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.disabled", mb: 1.25 }}
          >
            {t("OutcomePanel.differentiatorTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {differentiator}
          </Typography>
        </Box>
      </Box>

      {/* Provenance */}
      <Typography
        data-testid="outcome-provenance"
        variant="caption"
        color="text.disabled"
        sx={{ display: "block", mt: 3, fontStyle: "italic", lineHeight: 1.7 }}
      >
        {provenance}
      </Typography>
    </Box>
  );
}
